import React, { useMemo } from "react";
import Modal from "react-modal";
import {
  X,
  Tag,
  Hash,
  Package,
  MapPin,
  User,
  Phone,
  Mail,
  Building2,
  Warehouse,
} from "lucide-react";

const modalStyles = {
  overlay: {
    backgroundColor: "rgba(15, 23, 42, 0.55)",
    zIndex: 9999,
  },
  content: {
    inset: "auto",
    padding: 0,
    border: "none",
    background: "transparent",
    overflow: "visible",
  },
};

const toStr = (v, fb = "---") =>
  v === undefined || v === null || String(v).trim() === "" ? fb : String(v);

const tipoLabel = (tipo) => {
  const t = (tipo || "").toLowerCase();
  if (t.includes("principal") || t === "main") return "Principal";
  if (t.includes("secundario") || t === "secondary") return "Secundario";
  return tipo ? String(tipo) : "—";
};

const tipoPillClass = (tipo) => {
  const t = (tipo || "").toLowerCase();
  if (t.includes("principal") || t === "main") return "sdm-pill sdm-pill-blue";
  return "sdm-pill sdm-pill-gray";
};

export default function SucursalDetailModal({ isOpen, onClose, sucursal }) {
  const data = useMemo(() => {
    if (!sucursal) return null;

    return {
      codigo: toStr(sucursal.codigo, "—"),
      nombre: toStr(sucursal.nombre, "—"),
      tipo: tipoLabel(sucursal.tipo),

      direccion: toStr(sucursal.direccion, "—"),
      calle: toStr(sucursal.calle, "—"),
      numExterior: toStr(sucursal.numExterior, "—"),
      numInterior: toStr(sucursal.numInterior, "—"),
      colonia: toStr(sucursal.colonia, "—"),
      cp: toStr(sucursal.cp, "—"),
      municipio: toStr(sucursal.municipio, "—"),
      estadoDireccion: toStr(sucursal.estadoDireccion, "—"),
      pais: toStr(sucursal.pais, "—"),

      responsable: toStr(sucursal.responsable, "—"),
      telefono: toStr(sucursal.telefono, "—"),
      correo: toStr(sucursal.correo, "—"),
    };
  }, [sucursal]);

  if (!data) return null;

  return (
    <>
      <style>{`
        :root{
          --sdm-primary: #3B82F6;
          --sdm-text: #0f172a;
          --sdm-muted: #64748b;
          --sdm-border: #E5E7EB;
          --sdm-bg: #ffffff;
          --sdm-soft2: #F8FAFC;
          --sdm-radius: 16px;
        }

        .sdm-wrap{
          position: fixed;
          inset: 0;
          display:flex;
          align-items:center;
          justify-content:center;
          padding: 14px;
        }

        .sdm-card{
          width: min(840px, calc(100vw - 28px));
          max-height: 90vh;
          display:flex;
          flex-direction: column;
          background: var(--sdm-bg);
          border-radius: var(--sdm-radius);
          box-shadow: 0 24px 70px rgba(2,6,23,.30);
          overflow: hidden;
          border: 1px solid rgba(229,231,235,.95);
          font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, "Noto Sans", "Helvetica Neue", sans-serif;
          color: var(--sdm-text);
        }

        .sdm-head{
          padding: 16px 20px;
          border-bottom: 1px solid var(--sdm-border);
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap: 12px;
          background: #fff;
        }

        .sdm-head-left{
          display:flex;
          align-items:center;
          gap: 10px;
          min-width: 0;
          flex: 1;
        }

        .sdm-title{
          margin: 0;
          font-size: 22px;
          font-weight: 400;
          line-height: 1.15;
          letter-spacing: -0.02em;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .sdm-codepill{
          display:inline-flex;
          align-items:center;
          gap: 8px;
          padding: 5px 10px;
          border-radius: 10px;
          background: #F3F4F6;
          border: 1px solid #E5E7EB;
          color:#334155;
          font-size: 13px;
          white-space: nowrap;
          flex: 0 0 auto;
        }

        .sdm-xbtn{
          border:none;
          background: transparent;
          padding: 7px;
          border-radius: 10px;
          cursor:pointer;
          color:#475569;
          transition: background .15s ease, color .15s ease;
          flex: 0 0 auto;
        }
        .sdm-xbtn:hover{ background:#F3F4F6; color:#0f172a; }

        .sdm-body{
          flex: 1;
          overflow-y: auto;
          padding: 20px;
          background: #fff;
        }

        .sdm-section{
          margin-bottom: 18px;
        }

        .sdm-section-title{
          display:flex;
          align-items:center;
          gap: 10px;
          color:#6b7280;
          font-size: 14px;
          font-weight: 400;
          margin: 0 0 12px;
        }

        .sdm-cardbox{
          background: var(--sdm-soft2);
          border: 1px solid #F1F5F9;
          border-radius: 14px;
          padding: 16px;
        }

        .sdm-grid2{
          display:grid;
          grid-template-columns: 1fr;
          gap: 14px 18px;
        }
        @media (min-width: 768px){
          .sdm-grid2{
            grid-template-columns: 1fr 1fr;
          }
        }

        .sdm-grid3{
          display:grid;
          grid-template-columns: 1fr;
          gap: 14px 18px;
        }
        @media (min-width: 768px){
          .sdm-grid3{
            grid-template-columns: 2fr 1fr 1fr;
          }
        }

        .sdm-field{
          display:flex;
          flex-direction:column;
          gap: 6px;
          min-width: 0;
        }

        .sdm-label{
          font-size: 14px;
          color:#9CA3AF;
          font-weight: 400;
        }

        .sdm-value{
          display:flex;
          align-items:center;
          gap: 10px;
          font-size: 16px;
          color:#111827;
          font-weight: 400;
          min-width: 0;
        }

        .sdm-value-start{
          align-items:flex-start;
        }

        .sdm-ico{
          color:#9ca3af;
          flex: 0 0 auto;
        }

        .sdm-ico-top{
          margin-top: 2px;
        }

        .sdm-ellipsis{
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .sdm-pill{
          display:inline-flex;
          align-items:center;
          padding: 6px 12px;
          border-radius: 999px;
          font-size: 14px;
          font-weight: 500;
          border: 1px solid transparent;
          line-height: 1;
          white-space: nowrap;
        }
        .sdm-pill-blue{
          background:#DBEAFE;
          border-color:#BFDBFE;
          color:#1D4ED8;
        }
        .sdm-pill-gray{
          background:#F3F4F6;
          border-color:#E5E7EB;
          color:#334155;
        }

        .sdm-link{
          color:#111827;
          text-decoration: none;
        }
        .sdm-link:hover{
          text-decoration: underline;
        }

        .sdm-subsection{
          display:flex;
          align-items:center;
          gap: 8px;
          color:#6b7280;
          font-size: 14px;
          font-weight: 400;
          margin: 2px 0 16px;
        }

        .sdm-empty{
          visibility: hidden;
          pointer-events: none;
        }
      `}</style>

      <Modal
        isOpen={!!isOpen}
        onRequestClose={onClose}
        style={modalStyles}
        ariaHideApp={false}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
      >
        <div className="sdm-wrap" onMouseDown={onClose}>
          <div
            className="sdm-card"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="sdm-head">
              <div className="sdm-head-left">
                <h2 className="sdm-title">{data.nombre}</h2>

                <span className="sdm-codepill" title={data.codigo}>
                  <Tag size={14} className="sdm-ico" />
                  <span>{data.codigo}</span>
                </span>
              </div>

              <button
                className="sdm-xbtn"
                onClick={onClose}
                aria-label="Cerrar"
              >
                <X size={22} />
              </button>
            </div>

            <div className="sdm-body">
              <div className="sdm-section">
                <h3 className="sdm-section-title">
                  <Building2 size={16} className="sdm-ico" />
                  Información General
                </h3>

                <div className="sdm-cardbox">
                  <div className="sdm-grid2">
                    <div className="sdm-field">
                      <div className="sdm-label">Código</div>
                      <div className="sdm-value">
                        <Hash size={16} className="sdm-ico" />
                        <span className="sdm-ellipsis">{data.codigo}</span>
                      </div>
                    </div>

                    <div className="sdm-field">
                      <div className="sdm-label">Tipo</div>
                      <div className="sdm-value">
                        <Package size={16} className="sdm-ico" />
                        <span className={tipoPillClass(data.tipo)}>
                          {data.tipo}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div style={{ height: 14 }} />

                  <div className="sdm-grid2">
                    <div className="sdm-field">
                      <div className="sdm-label">Nombre</div>
                      <div className="sdm-value">
                        <Warehouse size={16} className="sdm-ico" />
                        <span className="sdm-ellipsis">{data.nombre}</span>
                      </div>
                    </div>
                    <div />
                  </div>

                  <div style={{ height: 18 }} />

                  <div className="sdm-subsection">
                    <MapPin size={16} className="sdm-ico" />
                    Dirección
                  </div>

                  {/* FILA 1 */}
                  <div className="sdm-grid3">
                    <div className="sdm-field">
                      <div className="sdm-label">Calle</div>
                      <div className="sdm-value">
                        <span className="sdm-ellipsis">{data.calle}</span>
                      </div>
                    </div>

                    <div className="sdm-field">
                      <div className="sdm-label">Núm. Exterior</div>
                      <div className="sdm-value">
                        <span className="sdm-ellipsis">{data.numExterior}</span>
                      </div>
                    </div>

                    <div className="sdm-field">
                      <div className="sdm-label">Núm. Interior</div>
                      <div className="sdm-value">
                        <span className="sdm-ellipsis">{data.numInterior}</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ height: 14 }} />

                  {/* FILA 2 */}
                  <div className="sdm-grid3">
                    <div className="sdm-field">
                      <div className="sdm-label">Colonia</div>
                      <div className="sdm-value">
                        <span className="sdm-ellipsis">{data.colonia}</span>
                      </div>
                    </div>

                    <div className="sdm-field">
                      <div className="sdm-label">C.P.</div>
                      <div className="sdm-value">
                        <span className="sdm-ellipsis">{data.cp}</span>
                      </div>
                    </div>

                    <div className="sdm-field sdm-empty">
                      <div className="sdm-label">Vacío</div>
                      <div className="sdm-value">
                        <span>—</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ height: 14 }} />

                  {/* FILA 3 */}
                  <div className="sdm-grid3">
                    <div className="sdm-field">
                      <div className="sdm-label">Municipio</div>
                      <div className="sdm-value">
                        <span className="sdm-ellipsis">{data.municipio}</span>
                      </div>
                    </div>

                    <div className="sdm-field">
                      <div className="sdm-label">Estado</div>
                      <div className="sdm-value">
                        <span className="sdm-ellipsis">
                          {data.estadoDireccion}
                        </span>
                      </div>
                    </div>

                    <div className="sdm-field sdm-empty">
                      <div className="sdm-label">Vacío</div>
                      <div className="sdm-value">
                        <span>—</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ height: 14 }} />

                  {/* FILA 4 */}
                  <div className="sdm-grid3">
                    <div className="sdm-field">
                      <div className="sdm-label">País</div>
                      <div className="sdm-value">
                        <span className="sdm-ellipsis">{data.pais}</span>
                      </div>
                    </div>

                    <div className="sdm-field sdm-empty">
                      <div className="sdm-label">Vacío</div>
                      <div className="sdm-value">
                        <span>—</span>
                      </div>
                    </div>

                    <div className="sdm-field sdm-empty">
                      <div className="sdm-label">Vacío</div>
                      <div className="sdm-value">
                        <span>—</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="sdm-section">
                <h3 className="sdm-section-title">
                  <User size={16} className="sdm-ico" />
                  Información de Contacto
                </h3>

                <div className="sdm-cardbox">
                  <div className="sdm-grid2">
                    <div className="sdm-field">
                      <div className="sdm-label">Responsable</div>
                      <div className="sdm-value">
                        <User size={16} className="sdm-ico" />
                        <span className="sdm-ellipsis">{data.responsable}</span>
                      </div>
                    </div>

                    <div className="sdm-field">
                      <div className="sdm-label">Teléfono</div>
                      <div className="sdm-value">
                        <Phone size={16} className="sdm-ico" />
                        <a className="sdm-link" href={`tel:${data.telefono}`}>
                          {data.telefono}
                        </a>
                      </div>
                    </div>
                  </div>

                  <div style={{ height: 14 }} />

                  <div className="sdm-field">
                    <div className="sdm-label">Correo Electrónico</div>
                    <div className="sdm-value">
                      <Mail size={16} className="sdm-ico" />
                      <a className="sdm-link" href={`mailto:${data.correo}`}>
                        {data.correo}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
